import { create } from 'zustand';
import { api } from './api';

const useCmsStore = create((set, get) => ({
  // ==========================================================================
  // STATE
  // ==========================================================================

  // Pages state
  pages: [],
  selectedPage: null,
  pagesLoading: false,
  pagesPagination: { page: 1, perPage: 20, total: 0, pages: 0 },

  // Posts state
  posts: [],
  selectedPost: null,
  postsLoading: false,
  postsPagination: { page: 1, perPage: 20, total: 0, pages: 0 },

  // Categories state
  categories: [],
  categoriesLoading: false,

  // Tags state
  tags: [],
  tagsLoading: false,

  // Authors state
  authors: [],
  authorsLoading: false,

  // Media state
  media: [],
  mediaLoading: false,
  mediaPagination: { page: 1, perPage: 50, total: 0, pages: 0 },

  // Revisions state
  revisions: [],
  revisionsLoading: false,

  // Redirects state
  redirects: [],
  redirectsLoading: false,
  redirectsPagination: { page: 1, perPage: 50, total: 0, pages: 0 },

  // Global state
  error: null,
  seoScore: null,

  // AI state
  aiAvailable: false,
  aiGenerating: false,

  // ==========================================================================
  // PAGES ACTIONS
  // ==========================================================================

  fetchPages: async (filters = {}) => {
    set({ pagesLoading: true, error: null });
    try {
      const params = new URLSearchParams();
      if (filters.page) params.append('page', filters.page);
      if (filters.per_page) params.append('per_page', filters.per_page);
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      if (filters.parent_id) params.append('parent_id', filters.parent_id);

      const response = await api.get(`/api/cms/pages?${params.toString()}`);
      set({
        pages: response.data.pages,
        pagesPagination: {
          page: response.data.page,
          perPage: response.data.per_page,
          total: response.data.total,
          pages: response.data.pages_count,
        },
        pagesLoading: false,
      });
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to fetch pages',
        pagesLoading: false,
      });
    }
  },

  fetchPage: async (pageId) => {
    set({ pagesLoading: true, error: null });
    try {
      const response = await api.get(`/api/cms/pages/${pageId}`);
      set({ selectedPage: response.data, pagesLoading: false });
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to fetch page',
        pagesLoading: false,
      });
      throw error;
    }
  },

  createPage: async (pageData) => {
    set({ pagesLoading: true, error: null });
    try {
      const response = await api.post('/api/cms/pages', pageData);
      const newPage = response.data;
      set(state => ({
        pages: [newPage, ...state.pages],
        selectedPage: newPage,
        pagesLoading: false,
      }));
      return newPage;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to create page',
        pagesLoading: false,
      });
      throw error;
    }
  },

  updatePage: async (pageId, pageData) => {
    set({ pagesLoading: true, error: null });
    try {
      const response = await api.put(`/api/cms/pages/${pageId}`, pageData);
      const updatedPage = response.data;
      set(state => ({
        pages: state.pages.map(p => p.id === pageId ? updatedPage : p),
        selectedPage: updatedPage,
        pagesLoading: false,
      }));
      return updatedPage;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to update page',
        pagesLoading: false,
      });
      throw error;
    }
  },

  deletePage: async (pageId) => {
    try {
      await api.delete(`/api/cms/pages/${pageId}`);
      set(state => ({
        pages: state.pages.filter(p => p.id !== pageId),
        selectedPage: state.selectedPage?.id === pageId ? null : state.selectedPage,
      }));
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to delete page' });
      throw error;
    }
  },

  publishPage: async (pageId) => {
    try {
      const response = await api.post(`/api/cms/pages/${pageId}/publish`);
      const updatedPage = response.data;
      set(state => ({
        pages: state.pages.map(p => p.id === pageId ? updatedPage : p),
        selectedPage: state.selectedPage?.id === pageId ? updatedPage : state.selectedPage,
      }));
      return updatedPage;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to publish page' });
      throw error;
    }
  },

  duplicatePage: async (pageId) => {
    try {
      const response = await api.post(`/api/cms/pages/${pageId}/duplicate`);
      const newPage = response.data;
      set(state => ({
        pages: [newPage, ...state.pages],
      }));
      return newPage;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to duplicate page' });
      throw error;
    }
  },

  setSelectedPage: (page) => set({ selectedPage: page }),
  clearSelectedPage: () => set({ selectedPage: null }),

  // ==========================================================================
  // POSTS ACTIONS
  // ==========================================================================

  fetchPosts: async (filters = {}) => {
    set({ postsLoading: true, error: null });
    try {
      const params = new URLSearchParams();
      if (filters.page) params.append('page', filters.page);
      if (filters.per_page) params.append('per_page', filters.per_page);
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      if (filters.category_id) params.append('category_id', filters.category_id);
      if (filters.author_id) params.append('author_id', filters.author_id);
      if (filters.is_featured !== undefined) params.append('is_featured', filters.is_featured);

      const response = await api.get(`/api/cms/posts?${params.toString()}`);
      set({
        posts: response.data.posts,
        postsPagination: {
          page: response.data.page,
          perPage: response.data.per_page,
          total: response.data.total,
          pages: response.data.pages_count,
        },
        postsLoading: false,
      });
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to fetch posts',
        postsLoading: false,
      });
    }
  },

  fetchPost: async (postId) => {
    set({ postsLoading: true, error: null });
    try {
      const response = await api.get(`/api/cms/posts/${postId}`);
      set({ selectedPost: response.data, postsLoading: false });
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to fetch post',
        postsLoading: false,
      });
      throw error;
    }
  },

  createPost: async (postData) => {
    set({ postsLoading: true, error: null });
    try {
      const response = await api.post('/api/cms/posts', postData);
      const newPost = response.data;
      set(state => ({
        posts: [newPost, ...state.posts],
        selectedPost: newPost,
        postsLoading: false,
      }));
      return newPost;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to create post',
        postsLoading: false,
      });
      throw error;
    }
  },

  updatePost: async (postId, postData) => {
    set({ postsLoading: true, error: null });
    try {
      const response = await api.put(`/api/cms/posts/${postId}`, postData);
      const updatedPost = response.data;
      set(state => ({
        posts: state.posts.map(p => p.id === postId ? updatedPost : p),
        selectedPost: updatedPost,
        postsLoading: false,
      }));
      return updatedPost;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to update post',
        postsLoading: false,
      });
      throw error;
    }
  },

  deletePost: async (postId) => {
    try {
      await api.delete(`/api/cms/posts/${postId}`);
      set(state => ({
        posts: state.posts.filter(p => p.id !== postId),
        selectedPost: state.selectedPost?.id === postId ? null : state.selectedPost,
      }));
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to delete post' });
      throw error;
    }
  },

  publishPost: async (postId) => {
    try {
      const response = await api.post(`/api/cms/posts/${postId}/publish`);
      const updatedPost = response.data;
      set(state => ({
        posts: state.posts.map(p => p.id === postId ? updatedPost : p),
        selectedPost: state.selectedPost?.id === postId ? updatedPost : state.selectedPost,
      }));
      return updatedPost;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to publish post' });
      throw error;
    }
  },

  setSelectedPost: (post) => set({ selectedPost: post }),
  clearSelectedPost: () => set({ selectedPost: null }),

  // ==========================================================================
  // CATEGORIES ACTIONS
  // ==========================================================================

  fetchCategories: async () => {
    set({ categoriesLoading: true, error: null });
    try {
      const response = await api.get('/api/cms/categories');
      set({ categories: response.data.categories || response.data, categoriesLoading: false });
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to fetch categories',
        categoriesLoading: false,
      });
    }
  },

  createCategory: async (categoryData) => {
    try {
      const response = await api.post('/api/cms/categories', categoryData);
      const newCategory = response.data;
      set(state => ({
        categories: [...state.categories, newCategory],
      }));
      return newCategory;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to create category' });
      throw error;
    }
  },

  updateCategory: async (categoryId, categoryData) => {
    try {
      const response = await api.put(`/api/cms/categories/${categoryId}`, categoryData);
      const updatedCategory = response.data;
      set(state => ({
        categories: state.categories.map(c => c.id === categoryId ? updatedCategory : c),
      }));
      return updatedCategory;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to update category' });
      throw error;
    }
  },

  deleteCategory: async (categoryId) => {
    try {
      await api.delete(`/api/cms/categories/${categoryId}`);
      set(state => ({
        categories: state.categories.filter(c => c.id !== categoryId),
      }));
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to delete category' });
      throw error;
    }
  },

  // ==========================================================================
  // TAGS ACTIONS
  // ==========================================================================

  fetchTags: async (search = '') => {
    set({ tagsLoading: true, error: null });
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await api.get(`/api/cms/tags${params}`);
      set({ tags: response.data.tags || response.data, tagsLoading: false });
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to fetch tags',
        tagsLoading: false,
      });
    }
  },

  createTag: async (tagData) => {
    try {
      const response = await api.post('/api/cms/tags', tagData);
      const newTag = response.data;
      set(state => ({
        tags: [...state.tags, newTag],
      }));
      return newTag;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to create tag' });
      throw error;
    }
  },

  updateTag: async (tagId, tagData) => {
    try {
      const response = await api.put(`/api/cms/tags/${tagId}`, tagData);
      const updatedTag = response.data;
      set(state => ({
        tags: state.tags.map(t => t.id === tagId ? updatedTag : t),
      }));
      return updatedTag;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to update tag' });
      throw error;
    }
  },

  deleteTag: async (tagId) => {
    try {
      await api.delete(`/api/cms/tags/${tagId}`);
      set(state => ({
        tags: state.tags.filter(t => t.id !== tagId),
      }));
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to delete tag' });
      throw error;
    }
  },

  // ==========================================================================
  // AUTHORS ACTIONS
  // ==========================================================================

  fetchAuthors: async () => {
    set({ authorsLoading: true, error: null });
    try {
      const response = await api.get('/api/cms/authors');
      set({ authors: response.data.authors || response.data, authorsLoading: false });
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to fetch authors',
        authorsLoading: false,
      });
    }
  },

  createAuthor: async (authorData) => {
    try {
      const response = await api.post('/api/cms/authors', authorData);
      const newAuthor = response.data;
      set(state => ({
        authors: [...state.authors, newAuthor],
      }));
      return newAuthor;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to create author' });
      throw error;
    }
  },

  updateAuthor: async (authorId, authorData) => {
    try {
      const response = await api.put(`/api/cms/authors/${authorId}`, authorData);
      const updatedAuthor = response.data;
      set(state => ({
        authors: state.authors.map(a => a.id === authorId ? updatedAuthor : a),
      }));
      return updatedAuthor;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to update author' });
      throw error;
    }
  },

  deleteAuthor: async (authorId) => {
    try {
      await api.delete(`/api/cms/authors/${authorId}`);
      set(state => ({
        authors: state.authors.filter(a => a.id !== authorId),
      }));
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to delete author' });
      throw error;
    }
  },

  // ==========================================================================
  // MEDIA ACTIONS
  // ==========================================================================

  fetchMedia: async (filters = {}) => {
    set({ mediaLoading: true, error: null });
    try {
      const params = new URLSearchParams();
      if (filters.page) params.append('page', filters.page);
      if (filters.per_page) params.append('per_page', filters.per_page);
      if (filters.media_type) params.append('media_type', filters.media_type);
      if (filters.folder) params.append('folder', filters.folder);
      if (filters.search) params.append('search', filters.search);

      const response = await api.get(`/api/cms/media?${params.toString()}`);
      set({
        media: response.data.media,
        mediaPagination: {
          page: response.data.page,
          perPage: response.data.per_page,
          total: response.data.total,
          pages: response.data.pages_count,
        },
        mediaLoading: false,
      });
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to fetch media',
        mediaLoading: false,
      });
    }
  },

  uploadMedia: async (file, metadata = {}) => {
    set({ mediaLoading: true, error: null });
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (metadata.alt_text) formData.append('alt_text', metadata.alt_text);
      if (metadata.title) formData.append('title', metadata.title);
      if (metadata.caption) formData.append('caption', metadata.caption);
      if (metadata.folder) formData.append('folder', metadata.folder);

      const response = await api.post('/api/cms/media', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newMedia = response.data;
      set(state => ({
        media: [newMedia, ...state.media],
        mediaLoading: false,
      }));
      return newMedia;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to upload media',
        mediaLoading: false,
      });
      throw error;
    }
  },

  updateMedia: async (mediaId, mediaData) => {
    try {
      const response = await api.put(`/api/cms/media/${mediaId}`, mediaData);
      const updatedMedia = response.data;
      set(state => ({
        media: state.media.map(m => m.id === mediaId ? updatedMedia : m),
      }));
      return updatedMedia;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to update media' });
      throw error;
    }
  },

  deleteMedia: async (mediaId) => {
    try {
      await api.delete(`/api/cms/media/${mediaId}`);
      set(state => ({
        media: state.media.filter(m => m.id !== mediaId),
      }));
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to delete media' });
      throw error;
    }
  },

  // ==========================================================================
  // REVISIONS ACTIONS
  // ==========================================================================

  fetchRevisions: async (contentType, contentId) => {
    set({ revisionsLoading: true, error: null });
    try {
      const response = await api.get(`/api/cms/revisions/${contentType}/${contentId}`);
      set({ revisions: response.data, revisionsLoading: false });
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to fetch revisions',
        revisionsLoading: false,
      });
    }
  },

  restoreRevision: async (revisionId) => {
    try {
      const response = await api.post(`/api/cms/revisions/${revisionId}/restore`);
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to restore revision' });
      throw error;
    }
  },

  compareRevisions: async (revisionIdA, revisionIdB) => {
    try {
      const response = await api.get(`/api/cms/revisions/compare?a=${revisionIdA}&b=${revisionIdB}`);
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to compare revisions' });
      throw error;
    }
  },

  // ==========================================================================
  // REDIRECTS ACTIONS
  // ==========================================================================

  fetchRedirects: async (filters = {}) => {
    set({ redirectsLoading: true, error: null });
    try {
      const params = new URLSearchParams();
      if (filters.page) params.append('page', filters.page);
      if (filters.per_page) params.append('per_page', filters.per_page);
      if (filters.search) params.append('search', filters.search);
      if (filters.include_inactive) params.append('include_inactive', 'true');

      const response = await api.get(`/api/cms/redirects?${params.toString()}`);
      set({
        redirects: response.data.redirects,
        redirectsPagination: {
          page: response.data.page,
          perPage: response.data.per_page,
          total: response.data.total,
          pages: response.data.pages_count,
        },
        redirectsLoading: false,
      });
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to fetch redirects',
        redirectsLoading: false,
      });
    }
  },

  createRedirect: async (redirectData) => {
    try {
      const response = await api.post('/api/cms/redirects', redirectData);
      const newRedirect = response.data;
      set(state => ({
        redirects: [newRedirect, ...state.redirects],
      }));
      return newRedirect;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to create redirect' });
      throw error;
    }
  },

  updateRedirect: async (redirectId, redirectData) => {
    try {
      const response = await api.put(`/api/cms/redirects/${redirectId}`, redirectData);
      const updatedRedirect = response.data;
      set(state => ({
        redirects: state.redirects.map(r => r.id === redirectId ? updatedRedirect : r),
      }));
      return updatedRedirect;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to update redirect' });
      throw error;
    }
  },

  deleteRedirect: async (redirectId) => {
    try {
      await api.delete(`/api/cms/redirects/${redirectId}`);
      set(state => ({
        redirects: state.redirects.filter(r => r.id !== redirectId),
      }));
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to delete redirect' });
      throw error;
    }
  },

  // ==========================================================================
  // SEO ACTIONS
  // ==========================================================================

  calculateSeoScore: async (content) => {
    try {
      const response = await api.post('/api/cms/seo/score', content);
      set({ seoScore: response.data });
      return response.data;
    } catch (error) {
      // Don't show auth errors to user - they'll be handled by logout flow
      if (error.response?.status === 401) {
        return null;
      }
      set({ error: error.response?.data?.error || 'Failed to calculate SEO score' });
      return null;
    }
  },

  // ==========================================================================
  // AI CONTENT GENERATION ACTIONS
  // ==========================================================================

  // AI state
  aiAvailable: false,
  aiGenerating: false,

  checkAIStatus: async () => {
    try {
      const response = await api.get('/api/cms/ai/status');
      set({ aiAvailable: response.data.available });
      return response.data.available;
    } catch (error) {
      set({ aiAvailable: false });
      return false;
    }
  },

  generateArticleWithAI: async (title, language = 'pt-BR', additionalContext = '') => {
    set({ aiGenerating: true, error: null });
    try {
      const response = await api.post('/api/cms/ai/generate/article', {
        title,
        language,
        additional_context: additionalContext,
      });
      set({ aiGenerating: false });
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to generate article with AI',
        aiGenerating: false,
      });
      throw error;
    }
  },

  generatePageWithAI: async (title, language = 'pt-BR', pageType = 'page', additionalContext = '') => {
    set({ aiGenerating: true, error: null });
    try {
      const response = await api.post('/api/cms/ai/generate/page', {
        title,
        language,
        page_type: pageType,
        additional_context: additionalContext,
      });
      set({ aiGenerating: false });
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to generate page with AI',
        aiGenerating: false,
      });
      throw error;
    }
  },

  improveTextWithAI: async (text, action = 'improve', language = 'pt-BR') => {
    set({ aiGenerating: true, error: null });
    try {
      const response = await api.post('/api/cms/ai/improve', {
        text,
        action,
        language,
      });
      set({ aiGenerating: false });
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to improve text with AI',
        aiGenerating: false,
      });
      throw error;
    }
  },

  generateMetaWithAI: async (title, contentSummary, language = 'pt-BR') => {
    set({ aiGenerating: true, error: null });
    try {
      const response = await api.post('/api/cms/ai/generate/meta', {
        title,
        content_summary: contentSummary,
        language,
      });
      set({ aiGenerating: false });
      return response.data;
    } catch (error) {
      set({
        error: error.response?.data?.error || 'Failed to generate meta with AI',
        aiGenerating: false,
      });
      throw error;
    }
  },

  // ==========================================================================
  // UTILITY ACTIONS
  // ==========================================================================

  clearError: () => set({ error: null }),
  clearSeoScore: () => set({ seoScore: null }),

  // Reset all state
  reset: () => set({
    pages: [],
    selectedPage: null,
    pagesLoading: false,
    pagesPagination: { page: 1, perPage: 20, total: 0, pages: 0 },
    posts: [],
    selectedPost: null,
    postsLoading: false,
    postsPagination: { page: 1, perPage: 20, total: 0, pages: 0 },
    categories: [],
    categoriesLoading: false,
    tags: [],
    tagsLoading: false,
    authors: [],
    authorsLoading: false,
    media: [],
    mediaLoading: false,
    mediaPagination: { page: 1, perPage: 50, total: 0, pages: 0 },
    revisions: [],
    revisionsLoading: false,
    redirects: [],
    redirectsLoading: false,
    redirectsPagination: { page: 1, perPage: 50, total: 0, pages: 0 },
    error: null,
    seoScore: null,
    aiAvailable: false,
    aiGenerating: false,
  }),
}));

export default useCmsStore;
